import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { useMcpSurfaceAccess } from '../lib/mcpAccess'
import { useSectionAccess } from '../lib/roleAccess'
import { apiFetch } from '../lib/api'
import type { SparkListResponse } from '../lib/types'
import { AgentIcon } from '../components/icons/AgentIcon'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { SettingsIcon } from '../components/icons/SettingsIcon'
import { MarketplaceIcon } from '../components/icons/MarketplaceIcon'
import { WorkflowsIcon } from '../components/icons/WorkflowsIcon'
import { AnalyticsIcon } from '../components/icons/AnalyticsIcon'
import { AuditLogIcon } from '../components/icons/AuditLogIcon'
import { WarningIcon } from '../components/icons/WarningIcon'

/* First-run onboarding checklist (R-NAV-3) — one entry per step, in display order. */
const ONBOARDING_STEPS = [
  { labelKey: 'home.onboarding.steps.createAgent', to: '/agents' },
  { labelKey: 'home.onboarding.steps.configureConnections', to: '/global-settings' },
  { labelKey: 'home.onboarding.steps.addKnowledge', to: '/global-settings' },
  { labelKey: 'home.onboarding.steps.manageAccess', to: '/global-settings' },
] as const

function HomeCard({
  title,
  desc,
  icon,
  to,
  preview = false,
}: {
  title: string
  desc: string
  icon: React.ReactNode
  to: string
  preview?: boolean
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={e => { if (e.key === 'Enter') navigate(to) }}
      className={preview ? 'home-card coming-soon' : 'home-card'}
    >
      {preview && (
        <span className="home-card-preview" title={t('common.previewTooltip')}>
          {t('common.preview')}
        </span>
      )}
      <div className="home-card-icon">{icon}</div>
      <div className="home-card-title">{title}</div>
      <div className="home-card-desc">{desc}</div>
    </div>
  )
}

export function Home() {
  const { userName, isGlobalAdmin, agentsContracted, entitlementsAvailable, entitlementsPending } = useAuth()
  // MS-866 AC6 — replaces home-columns with the entitlements-error banner
  // (Decision 3: not while merely pending, so a cached session's first paint
  // doesn't flash the degraded state before the mount probe resolves).
  const entitlementsDegraded = entitlementsAvailable === false && !entitlementsPending
  const { canSeeMcpSurfaces } = useMcpSurfaceAccess()
  const { canOpen } = useSectionAccess()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [onboardingDismissed, setOnboardingDismissed] = useState(
    () => Boolean(localStorage.getItem('onboarding_dismissed'))
  )
  const { data: sparksData, isSuccess: sparksLoaded } = useQuery({
    queryKey: ['agents', 'onboarding-check'],
    queryFn: () => apiFetch<SparkListResponse>('/api/v1/agents?limit=1'),
    staleTime: 60_000,
    // Gated on `canOpen('agents')` for the reason stated in lib/roleAccess.ts:8-10 —
    // a role without access must not be sent to an endpoint that will only 403.
    // GET /agents requires Editor (api/v1/sparks.py list_sparks), so a role without
    // it would 403 here, then 403 again on the queryClient's `retry: 1`.
    enabled: !onboardingDismissed && canOpen('agents'),
  })

  const showOnboarding =
    !onboardingDismissed &&
    sparksLoaded &&
    sparksData?.items.length === 0

  function dismissOnboarding() {
    localStorage.setItem('onboarding_dismissed', '1')
    setOnboardingDismissed(true)
  }

  const hour = new Date().getHours()
  const greetingKey = hour < 12
    ? 'home.greeting.morning'
    : hour < 18
      ? 'home.greeting.afternoon'
      : 'home.greeting.evening'
  const name = userName?.split(' ')[0] ?? 'there'

  /* Built as data so an empty group's section label can be dropped (MS-741
     AC3.7), mirroring AppShell's nav-group pattern. Each card is
     `canOpen(section) && ...` so a role-hidden surface disappears entirely —
     hide, not preview — while the Agents card stays visible and gets the
     Preview badge for the subscription gate instead (AC2). */
  const homeGroups = [
    {
      label: t('home.sections.configure'),
      items: [
        canOpen('agents') && (
          <HomeCard
            key="agents"
            to="/agents"
            title={t('home.cards.agents.title')}
            desc={t('home.cards.agents.desc')}
            icon={<AgentIcon size={20} />}
            preview={!agentsContracted}
          />
        ),
        canOpen('mcp') && isGlobalAdmin && (
          <HomeCard
            key="mcp"
            to="/mcp"
            title={t('home.cards.mambuMcp.title')}
            desc={t('home.cards.mambuMcp.desc')}
            icon={<HeartbeatIcon size={20} />}
          />
        ),
        canOpen('global') && (
          <HomeCard
            key="global-settings"
            to="/global-settings"
            title={t('home.cards.globalSettings.title')}
            desc={t('home.cards.globalSettings.desc')}
            icon={<SettingsIcon size={20} />}
          />
        ),
      ],
    },
    {
      label: t('home.sections.extend'),
      items: [
        canOpen('marketplace') && (
          <HomeCard
            key="marketplace"
            to="/marketplace"
            title={t('home.cards.marketplace.title')}
            desc={t('home.cards.marketplace.desc')}
            preview
            icon={<MarketplaceIcon size={20} />}
          />
        ),
        canOpen('workflows') && (
          <HomeCard
            key="workflows"
            to="/workflows"
            title={t('home.cards.workflows.title')}
            desc={t('home.cards.workflows.desc')}
            preview
            icon={<WorkflowsIcon size={20} />}
          />
        ),
      ],
    },
    {
      label: t('home.sections.observe'),
      items: [
        canOpen('analytics') && (
          <HomeCard
            key="agents-analytics"
            to="/agents-analytics"
            title={t('home.cards.analytics.title')}
            desc={t('home.cards.analytics.desc')}
            preview
            icon={<AnalyticsIcon size={20} />}
          />
        ),
        /* MCP Analytics quick-action card, per the prototype's mcp-gated Observe
           card (agent-studio.html:3544). Carries the SAME gate as its nav item —
           an ungated card pointing at a gated route would just bounce the user
           back here. */
        canOpen('mcp-analytics') && canSeeMcpSurfaces && (
          <HomeCard
            key="mcp-analytics"
            to="/mcp-analytics"
            title={t('home.cards.mcpAnalytics.title')}
            desc={t('home.cards.mcpAnalytics.desc')}
            icon={<AnalyticsIcon size={20} />}
          />
        ),
        canOpen('audit') && (
          <HomeCard
            key="audit-log"
            to="/audit-log"
            title={t('home.cards.auditLog.title')}
            desc={t('home.cards.auditLog.desc')}
            icon={<AuditLogIcon size={20} />}
          />
        ),
      ],
    },
  ]
    .map(g => ({ ...g, items: g.items.filter(Boolean) }))
    .filter(g => g.items.length > 0)

  return (
    <div className="home-view">
      <div className="home-header">
        <div className="home-greeting">{t(greetingKey)}, {name}</div>
        <div className="home-subtext">{t('home.subtitle')}</div>
      </div>

      {entitlementsDegraded && (
        <div style={{ flexShrink: 0, padding: '8px 40px 0' }}>
          <div style={{ maxWidth: 560, background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 12, padding: '18px 20px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }}>
              <WarningIcon size={22} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--warn-fg-strong)', marginBottom: 4 }}>
                {t('entitlementsError.heading')}
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.55 }}>
                {t('entitlementsError.body')}
              </p>
            </div>
          </div>
        </div>
      )}

      {!entitlementsDegraded && (
      <div className="home-columns">
      {showOnboarding && (
        <section className="home-onboarding-card" aria-label={t('home.onboarding.title')}>
          <div className="home-onboarding-header">
            <div>
              <div className="home-onboarding-title">{t('home.onboarding.title')}</div>
              <div className="home-onboarding-subtitle">{t('home.onboarding.subtitle')}</div>
            </div>
            <button
              type="button"
              className="home-alert-dismiss"
              onClick={dismissOnboarding}
              aria-label={t('home.onboarding.dismissAriaLabel')}
            >
              {t('home.onboarding.dismiss')}
            </button>
          </div>
          <ol className="home-onboarding-steps">
            {ONBOARDING_STEPS.map(step => (
              <li key={step.labelKey}>
                <button type="button" className="home-onboarding-step" onClick={() => navigate(step.to)}>
                  {t(step.labelKey)}
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}

      {homeGroups.map(group => (
        <Fragment key={group.label}>
          <div className="home-section-label">{group.label}</div>
          <div className="home-grid">{group.items}</div>
        </Fragment>
      ))}
      </div>
      )}
    </div>
  )
}
